import { Point } from './point.interface';

export const getAngleRadiant = (p1: Point, p2: Point) => {
  if (!p1) {
    p1 = { x: 0, y: 0 };
  }
  if (!p2) {
    p2 = { x: 0, y: 0 };
  }

  let angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
  if (angle < 0) {
    angle = 2 * Math.PI + angle;
  }
  return angle;
};
const getAngleDegree = (rad: number) => {
  return (rad * 180) / Math.PI;
};

const movePointByAngle = (point: Point, angle: number, distance: number): Point => {
  return {
    x: point.x + Math.sin(angle) * distance,
    y: point.y - Math.cos(angle) * distance,
  };
};

const getMaxDistanceToLine = (points: Point[], startIndex: number, steps: number, relativeRotationRadiant: number) => {
  let heightAbs = 0;
  let heightReal = 0;

  const startPoint = points[startIndex];
  for (let i = 1; i < steps; i++) {
    let index = startIndex + i;
    if (index >= points.length) {
      index -= points.length;
    }

    const point = points[index];
    const angleRad = relativeRotationRadiant - getAngleRadiant(startPoint, point);

    const l = getLineLength(startPoint, point);
    const hReal = l * Math.sin(angleRad);
    const hAbs = Math.abs(hReal);

    if (hAbs > heightAbs) {
      heightAbs = hAbs;
      heightReal = hReal;
    }
  }

  return heightReal;
};

export const calcArea = (points: Point[]): number => {
  let total = 0;

  for (let i = 0, l = points.length; i < l; i++) {
    const addX = points[i].x;
    const addY = points[i === points.length - 1 ? 0 : i + 1].y;
    const subX = points[i === points.length - 1 ? 0 : i + 1].x;
    const subY = points[i].y;

    total += addX * addY * 0.5;
    total -= subX * subY * 0.5;
  }

  return Math.abs(total);
};

export const getLineLength = (start: Point, end: Point): number => {
  const xMax = Math.max(end.x, start.x);
  const xMin = Math.min(end.x, start.x);
  const yMax = Math.max(end.y, start.y);
  const yMin = Math.min(end.y, start.y);
  const length = Math.sqrt(Math.pow(xMax - xMin, 2) + Math.pow(yMax - yMin, 2));

  return length;
};

export const getMaxLength = (points: Point[]): [number, number, number] => {
  let maxLength = 0;
  let startIndex = 0;
  let endIndex = 0;

  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const length = getLineLength(points[i], points[j]);

      if (length > maxLength) {
        maxLength = length;
        startIndex = i;
        endIndex = j;
      }
    }
  }

  return [startIndex, endIndex, maxLength];
};

export const getBoundingBox = (
  points: Point[]
): {
  origin: Point;
  angleDeg: number;
  width: number;
  height: number;
} => {
  const [startIndex, endIndex, maxLength] = getMaxLength(points);
  const [startPoint, endPoint] = [points[startIndex], points[endIndex]];

  // rotiation angle of longest distance
  const rotationAngleRad = getAngleRadiant(startPoint, endPoint);

  /*
  Get height above and below (90 degree each) longest distance line.
  Real distances mean, there is one positive and one negative distance.
  We will need to move the bounding box down by the negative distance.
  */
  const height1Real = getMaxDistanceToLine(points, startIndex, endIndex - startIndex, rotationAngleRad);
  const height2Real = getMaxDistanceToLine(points, endIndex, points.length - (endIndex - startIndex), rotationAngleRad);

  // StartPoint for the bounding box
  const relativeStartPoint = movePointByAngle(startPoint, rotationAngleRad, Math.min(height1Real, height2Real));

  return {
    origin: relativeStartPoint,
    angleDeg: getAngleDegree(rotationAngleRad),
    width: maxLength,
    height: Math.abs(height1Real) + Math.abs(height2Real),
  };
};
